/**
 * Offline queue for storing and retrying failed Convex mutations
 */

import { OFFLINE_QUEUE_MUTATION_DROPPED_EVENT } from '../constants/events';

const OFFLINE_QUEUE_KEY = 'budget_app_offline_queue';

export type QueuedMutation = {
  id: string;
  type: 'createBudget' | 'updateBudget' | 'deleteBudget' | 'createVersion' | 'setDefaultBudget';
  /** Sent verbatim to the Convex mutation */
  payload: Record<string, unknown>;
  /** Client-side bookkeeping (e.g. localBudgetId for createBudget) — never sent to Convex */
  meta?: Record<string, unknown>;
  timestamp: number;
  retries: number;
};

const MAX_RETRIES = 3;
const MAX_QUEUE_SIZE = 100;

export function getOfflineQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveOfflineQueue(queue: QueuedMutation[]): void {
  try {
    if (queue.length > MAX_QUEUE_SIZE) {
      const droppedCount = queue.length - MAX_QUEUE_SIZE;
      console.warn(`Offline queue overflow: dropping ${droppedCount} oldest mutations`);
      window.dispatchEvent(
        new CustomEvent(OFFLINE_QUEUE_MUTATION_DROPPED_EVENT, {
          detail: { reason: 'overflow', count: droppedCount },
        })
      );
    }
    // Keep the newest MAX_QUEUE_SIZE entries — the queue is ordered oldest → newest
    const limitedQueue = queue.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(limitedQueue));
  } catch (error) {
    console.error('Failed to save offline queue:', error);
  }
}

export function addToOfflineQueue(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retries'>): void {
  const queue = getOfflineQueue();
  const newMutation: QueuedMutation = {
    ...mutation,
    id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    timestamp: Date.now(),
    retries: 0,
  };
  queue.push(newMutation);
  saveOfflineQueue(queue);
}

export function removeFromOfflineQueue(id: string): void {
  const queue = getOfflineQueue();
  const filtered = queue.filter((m) => m.id !== id);
  saveOfflineQueue(filtered);
}

export function incrementRetry(id: string): void {
  const queue = getOfflineQueue();
  const mutation = queue.find((m) => m.id === id);
  if (mutation) {
    mutation.retries += 1;
    if (mutation.retries >= MAX_RETRIES) {
      // Remove if max retries exceeded
      removeFromOfflineQueue(id);
      console.warn(`Mutation ${id} (${mutation.type}) exceeded max retries and was removed from queue`);
      window.dispatchEvent(
        new CustomEvent(OFFLINE_QUEUE_MUTATION_DROPPED_EVENT, {
          detail: { reason: 'max_retries', mutationType: mutation.type },
        })
      );
    } else {
      saveOfflineQueue(queue);
    }
  }
}

export function clearOfflineQueue(): void {
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
}
